import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';

// Components
import Navbar from '../navbar/Navbar';
import Loader from '../loader/Loader';
import SettingsSidebar from '../sidebar/SettingsSidebar';

// Action imports
import { updateProfile, getUser } from '../../actions/auth';

// CSS
import '../../css/landing/landing.css';
import '../../css/userDetails/apiUsage.css';

//Util Imports
//Utils
import { downloadFullReport } from '../../components/utils/csvUtils';

const APIUsage = ({
  auth: { isAuthenticated, user },
  getUser,
  updateProfile,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [apiUsageData, setApiUsageData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchAPIUsage = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URI}/ecomnext/api/api-usage?page_no=${currentPage}`
        );

        if (response.data?.api_usage) {
          setApiUsageData(response.data.api_usage);
          setTotalPages(Math.ceil(response.data.totalRecords / 10));
        }
      } catch (error) {
        console.error('Error fetching API usage:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAPIUsage();
  }, [currentPage]);

  const handlePrevPage = () => {
    setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));
  };

  const handleNextPageClick = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handleDownloadReport = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/ecomnext/api/api-usage/full-report`,
        { responseType: 'json' }
      );

      if (response.data?.report) {
        downloadFullReport(response.data.report, fields, constructFileName());
        toast.success('Report downloaded successfully!');
      } else {
        toast.error('Failed to download the report. Please try again.');
      }
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('An error occurred while downloading the report.');
    } finally {
      setIsLoading(false);
    }
  };

  const fields = [
    { key: 'api_name', label: 'API Name' },
    { key: 'tokens_used', label: 'Token Used' },
    { key: 'timestamp', label: 'Timestamp(UTC)' },
  ];

  const constructFileName = () => {
    const currentDate = new Date()
      .toISOString()
      .split('T')[0]
      .replace(/-/g, '');
    return `API_usage_full_report_${currentDate}.csv`;
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div className='flex min-h-screen'>
          {/* Sidebar */}
          <div className='w-64'>
            <SettingsSidebar />
          </div>

          {/* Main Content Area */}
          <div className='flex-1 flex flex-col'>
            <Navbar />

            <div className='api-usage-page-wrapper flex-1 bg-white p-6'>
              <div className='flex items-center justify-between'>
                <h1 className='api-usage-page-heading'>API Usage</h1>
                <button
                  className={`api-usage-report-download-button px-2 py-2 ${
                    !apiUsageData.length || isLoading
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                  onClick={handleDownloadReport}
                  disabled={!apiUsageData.length || isLoading}
                >
                  {isLoading ? (
                    <i className='fa-solid fa-spinner fa-spin mr-2'></i>
                  ) : (
                    <i className='fa-solid fa-arrow-down mr-2'></i>
                  )}
                  {isLoading ? 'Downloading...' : 'Download full report'}
                </button>
              </div>
              <hr />

              <div className='mt-6 shadow-lg overflow-hidden rounded-lg'>
                <table className='w-full text-center'>
                  <thead className='api-usage-table-head border-b border-gray-200 tracking-wider'>
                    <tr>
                      <th className='py-3 px-4 font-medium'>API Name</th>
                      <th className='py-3 px-4 font-medium'>Tokens Used</th>
                      <th className='py-3 px-4 font-medium'>Time</th>
                    </tr>
                  </thead>
                  <tbody className='text-gray-600 text-sm divide-y divide-gray-200'>
                    {apiUsageData.map((usage) => (
                      <tr key={usage.usage_id}>
                        <td className='py-3 px-4'>{usage.api_name}</td>
                        <td className='py-3 px-4'>{usage.tokens_used}</td>
                        <td className='py-3 px-4'>
                          {new Date(usage.timestamp).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className='flex justify-center gap-4 mt-4 mb-4 items-center'>
                <button
                  className='prev-button disabled:cursor-not-allowed'
                  disabled={currentPage === 1}
                  onClick={handlePrevPage}
                >
                  <i className='fa-solid fa-angle-left mr-1'></i> PREV
                </button>
                <span className='page-number-display px-4 py-2 rounded-md font-semibold'>
                  {currentPage}
                </span>

                <button
                  className='next-button disabled:cursor-not-allowed'
                  onClick={handleNextPageClick}
                  disabled={currentPage >= totalPages}
                >
                  NEXT <i className='fa-solid fa-angle-right ml-1'></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { getUser, updateProfile })(APIUsage);
